import React from 'react';
import { Box } from '@mui/material';
import {
    PieChart,
    Pie,
    ResponsiveContainer,
    Tooltip,
    Legend,
    Cell,
} from 'recharts';
import { CHART_DIMENSIONS } from './styles';

// Diagramos nustatymai
const CHART_SETTINGS = {
    // Diagramos dydis
    MIN_HEIGHT: 400,
    WIDTH_TO_HEIGHT_RATIO: 0.9,
    MAX_HEIGHT: 500,
    
    // Skritulio nustatymai
    INNER_RADIUS: 100,
    OUTER_RADIUS: 180,
    PADDING_ANGLE: 3,
    
    // Procentų pozicionavimas
    LABEL_DISTANCE_RATIO: 1.2,  // Padidintas atstumas nuo skritulio centro
    MIN_PERCENT_TO_SHOW: 0.01,   // Sumažintas minimalus procentas
    
    // Linijų stilius
    LABEL_LINE: {
        stroke: '#666',
        strokeWidth: 1,
        strokeDasharray: '2,2',
        margin: 8
    },
    
    // Teksto stilius
    LABEL_FONT_SIZE: '18px',
    LEGEND_FONT_SIZE: '18px'
};

// Spalvų paletė
const PIE_COLORS = [
    '#4A90E2', // Mėlyna - geras kontrastas, tinka daltonizmui
    '#8B7DA9', // Levandų - neutrali, rami spalva
    '#9B6B9E', // Švelniai violetinė - gerai matoma
    '#F4A261', // Šviesiai oranžinė - šilta, bet ne per ryški
    '#45B7D1', // Turkio - šviesi ir aiški
    '#B784A7', // Šviesiai rožinė - švelni, bet kontrastinga
];

export const RadialChartComponent = ({ data, CustomTooltip }) => {
    const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });
    const containerRef = React.useRef(null);

    React.useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const { width } = containerRef.current.getBoundingClientRect();
                const height = Math.min(width * CHART_SETTINGS.WIDTH_TO_HEIGHT_RATIO, CHART_SETTINGS.MAX_HEIGHT); // Aukštis bus 60% pločio, bet ne daugiau 500px
                setContainerSize({ width, height });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    const processedData = data.filter(item => item.value > 0);
    const total = processedData.reduce((sum, item) => sum + item.value, 0);

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value, index }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * CHART_SETTINGS.LABEL_DISTANCE_RATIO;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        if (percent < CHART_SETTINGS.MIN_PERCENT_TO_SHOW) return null;

        // Pakoreguojame x poziciją, kad skaičiai būtų toliau
        const adjustedX = x > cx 
            ? x + 15  // Dešinėje pusėje pridedame tarpą
            : x - 15; // Kairėje pusėje atimame tarpą

        return (
            <g>
                <text
                    x={adjustedX}
                    y={y}
                    fill="#000"
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                    style={{
                        fontSize: CHART_SETTINGS.LABEL_FONT_SIZE,
                        fontWeight: '500',
                    }}
                >
                    {`${Math.round(value)}%`}
                </text>
            </g>
        );
    };

    return (
        <Box ref={containerRef} sx={{ width: '100%', minHeight: CHART_SETTINGS.MIN_HEIGHT }}>
            <ResponsiveContainer width="100%" height={containerSize.height}>
                <PieChart>
                <Pie
                    data={processedData}
                    dataKey="value"
                    nameKey="text"
                    cx="50%"
                    cy="50%"
                    innerRadius={CHART_SETTINGS.INNER_RADIUS}
                    outerRadius={CHART_SETTINGS.OUTER_RADIUS}
                    label={renderCustomLabel}
                    paddingAngle={CHART_SETTINGS.PADDING_ANGLE}
                    labelLine={CHART_SETTINGS.LABEL_LINE}
                >
                    {processedData.map((entry, index) => (
                        <Cell 
                            key={`cell-${index}`} 
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                            strokeWidth={1}
                            stroke="#fff"
                        />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    iconSize={14}
                    wrapperStyle={{
                        paddingLeft: '25px',
                        maxWidth: '40%',
                        fontSize: CHART_SETTINGS.LEGEND_FONT_SIZE,
                        fontWeight: '500',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word'
                    }}
                />
                </PieChart>
            </ResponsiveContainer>
        </Box>
    );
};
